import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { MultiBuyerCmpAuth } from '../../../auth/auth.interface';
import { AuthService } from '../../../auth/auth.service';
import {
  AccessControlConfig,
  AccessibleEntity,
  isMultiBuyerRole,
  MultiBuyerRole,
  MULTI_BUYER_ROLES_NUM_MASK,
} from './interfaces/multi-buyer-company.interface';
// Used when no config has been loaded yet — denies everything until a real config arrives.
export function loadingAccessConfig(): AccessControlConfig {
  return {
    clientId: 0,
    clientName: 'none',
    accessibleEntities: [],
    buyerAcssLvls: 0,
    capabilities: {
      canSeeMarginAndBank: false,
      canCreateTemplate: false,
      canManageBuyerSettings: false,
      hasAccessToTemplate: false,
      canSeeCustomTemplates: false,
      canManageSupplierSettings: false,
      canSeeSupplierBlends: false,
      canAccessMasterTemplate: false,
      canSeeOperatorProjectManager: false,
      canSeeCrmId: false,
    },
    multiBuyerCmps: [],
  };
}

// Used for non-multi-buyer users after login — they have full access by design.
export function defaultAccessConfig(): AccessControlConfig {
  return {
    clientId: 0,
    accessibleEntities: [],
    buyerAcssLvls: 0,
    clientName: 'none',
    multiBuyerCmps: [0],
    capabilities: {
      canSeeMarginAndBank: true,
      canCreateTemplate: true,
      canManageBuyerSettings: true,
      hasAccessToTemplate: true,
      canSeeCustomTemplates: false,
      canManageSupplierSettings: true,
      canSeeSupplierBlends: true,
      canAccessMasterTemplate: false,
      canSeeOperatorProjectManager: false,
      canSeeCrmId: false,
    },
  };
}

@Injectable({ providedIn: 'root' })
export class MultiBuyerStateService {
  private readonly _baseUrl = environment.pureSpectrum.url;

  private readonly _selectedMultiBuyerCompany$ = new BehaviorSubject<
    MultiBuyerCmpAuth | undefined
  >(undefined);
  readonly selectedMultiBuyerCompany$ =
    this._selectedMultiBuyerCompany$.asObservable();

  private readonly _accessControlConfig$ =
    new BehaviorSubject<AccessControlConfig>(loadingAccessConfig());
  readonly accessControlConfig$ = this._accessControlConfig$.asObservable();

  private readonly _loggedInAsMultiBuyer$ = new BehaviorSubject<boolean>(false);
  readonly loggedInAsMultiBuyer$ = this._loggedInAsMultiBuyer$.asObservable();

  private readonly _multiBuyerModalCloseRequests$ = new Subject<{
    source: 'dashboard' | 'auth' | 'unknown';
  }>();
  readonly multiBuyerModalCloseRequests$ =
    this._multiBuyerModalCloseRequests$.asObservable();

  constructor(
    private readonly _http: HttpClient,
    private readonly _injector: Injector
  ) {}

  private get _auth(): AuthService {
    return this._injector.get(AuthService);
  }

  get selectedMultiBuyerCompany(): MultiBuyerCmpAuth | undefined {
    return this._selectedMultiBuyerCompany$.getValue();
  }

  get accessControlConfig(): AccessControlConfig {
    return this._accessControlConfig$.getValue();
  }

  get multiBuyerCmpsIds(): number[] {
    const { accessibleEntities, multiBuyerCmps } = this.accessControlConfig;
    const entities: AccessibleEntity[] = accessibleEntities?.length
      ? accessibleEntities
      : multiBuyerCmps.map((id) => ({ id, type: 'owned' }));
    return this._filterEntities(entities);
  }

  private _filterEntities(entities: AccessibleEntity[]): number[] {
    const includeSP = this.isLoggedInAsMbManager();
    return entities
      .filter(
        (e) => e.type === 'owned' || (includeSP && e.type === 'servicePartner')
      )
      .map((e) => e.id);
  }

  isLoggedInAsMultiBuyer(): boolean {
    if (this.accessControlConfig.userRole) {
      return isMultiBuyerRole(this.accessControlConfig.userRole);
    }
    const user = this._auth.user;
    return (
      user?.buyerAcssLvls === 'masterMultiBuyer' ||
      user?.buyerAcssLvls === 'multiBuyer'
    );
  }

  isLoggedInAsMasterMultiBuyer(): boolean {
    if (this.accessControlConfig.userRole) {
      return this.accessControlConfig.userRole === 'masterMultiBuyer';
    }
    return this._auth.user?.buyerAcssLvls === 'masterMultiBuyer';
  }

  isLoggedInAsMbManager(): boolean {
    if (this.accessControlConfig.userRole) {
      return this.accessControlConfig.userRole === 'mbManager';
    }
    const user = this._auth.user;
    return user?.buyerAcssLvls === 'multiBuyer' && !!user?.isMBManager;
  }

  isSelectedMultiBuyerCompany(): boolean {
    return !!this._selectedMultiBuyerCompany$.getValue();
  }

  setSelectedCompany(company: MultiBuyerCmpAuth | undefined): void {
    this._selectedMultiBuyerCompany$.next(company);
  }

  setAccessControlConfig(config: AccessControlConfig): void {
    this._accessControlConfig$.next(config);
    this._loggedInAsMultiBuyer$.next(this.isLoggedInAsMultiBuyer());
  }

  getNumericMultiBuyerRole(userRole: MultiBuyerRole): number {
    return MULTI_BUYER_ROLES_NUM_MASK[userRole];
  }

  requestCloseMultiBuyerModal(request?: {
    source?: 'dashboard' | 'auth' | 'unknown';
  }): void {
    this._multiBuyerModalCloseRequests$.next({
      source: request?.source ?? 'unknown',
    });
  }

  getAccessControlConfig(
    clientId: number,
    userRole: MultiBuyerRole
  ): Observable<AccessControlConfig> {
    const convertedMultiBuyerUserRole = this.getNumericMultiBuyerRole(userRole);
    return this._http
      .get<AccessControlConfig>(
        `${this._baseUrl}/buyers/v2/configs/${clientId}/${convertedMultiBuyerUserRole}`
      )
      .pipe(map((config) => ({ ...config, userRole })));
  }
}
